import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";

import { Routes } from "../../navigation";
import { DashboardPageCmp, DashboardPageProps } from "./types";
import { Grid } from "ui/components/Grid";
import { AvailableWidget, GridRef } from "ui/components/Grid/types";
import { clampLayoutsToWidgetMinimums, generateGridFromTemplate } from "ui/components/Grid/helper";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import {
  DASHBOARD_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Button } from "ui/components/Button/Button";
import { useTranslate } from "helpers";
import { getActiveUser } from "services/api/users";

import { DASHBOARD_AVAILABLE_WIDGETS } from "services/api/dashboard/consts";

import WidgetEditCard from "./components/WidgetEditCard/WidgetEditCard";
import VehicleEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/VehicleEditCard";
import { DashboardDateRangeProvider, DashboardDateRangeChip } from "./context/DashboardDateRangeContext";
import { DASHBOARD_STATE_KEY, resetOutdatedDashboardState } from "./storage";

// Prije prvog čitanja — zastarjeli raspored se odbacuje, ne krpa.
resetOutdatedDashboardState();


const DashboardPage: DashboardPageCmp = (props: DashboardPageProps) => {
  const { t } = useTranslate();
  const { user } = useSelector(getActiveUser);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const gridRef = useRef<GridRef>(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t("admin.dashboard.greeting.morning");
    if (hour < 18) return t("admin.dashboard.greeting.afternoon");
    return t("admin.dashboard.greeting.evening");
  };

  const userName = user ? `${user.firstName} ${user.lastName}` : "";

  //1. pokušaj dohvatiti spremljeni state
  const savedState = JSON.parse(
    localStorage.getItem(DASHBOARD_STATE_KEY) || "null"
  );

  //2. inicijalizacija
  const initialUserWidgets =
    savedState?.userWidgets ?? DASHBOARD_AVAILABLE_WIDGETS(t).map((w) => w.id);
  const savedLayouts = savedState?.layouts ?? null;

  // Sub-widgets: default all active
  const defaultSubWidgets: Record<string, string[]> = {};
  DASHBOARD_AVAILABLE_WIDGETS(t).forEach((w) => {
    if (w.subWidgets) {
      defaultSubWidgets[w.id] = w.subWidgets.map((s) => s.id);
    }
  });
  const initialSubWidgets = savedState?.subWidgets ?? defaultSubWidgets;

  const [userWidgets, setUserWidgets] = useState<string[]>(initialUserWidgets);
  const [userSubWidgets, setUserSubWidgets] = useState<Record<string, string[]>>(initialSubWidgets);
  const [template, setTemplate] = useState<GridTemplate>(
    savedState?.template ?? "three-columns"
  );

  //3. generiraj grid
  const templateConfig = useMemo(
    () => GRID_TEMPLATES.find((tpl) => tpl.id === template) || GRID_TEMPLATES[0],
    [template]
  );

  const { widgets, layouts } = useMemo(() => {
    const availableWidgets = userWidgets
      .map((id) => DASHBOARD_AVAILABLE_WIDGETS(t).find((w) => w.id === id))
      .filter(Boolean) as AvailableWidget[];

    return generateGridFromTemplate(templateConfig, availableWidgets);
  }, [userWidgets, t, templateConfig]);

  // Spremljeno slaganje se zadržava, ali se kartice manje od minimuma podignu.
  // Breakpointi kojih u spremljenom stanju nema uzimaju se iz predloška.
  const initialLayouts = savedLayouts
    ? { ...layouts, ...clampLayoutsToWidgetMinimums(savedLayouts, widgets) }
    : layouts;

  //4. sinkronizacija (widgets + layouts + template)
  const saveDashboardState = useCallback(
    (layoutsToSave?: any) => {
      localStorage.setItem(
        DASHBOARD_STATE_KEY,
        JSON.stringify({
          userWidgets,
          subWidgets: userSubWidgets,
          template,
          layouts: layoutsToSave ?? savedState?.layouts ?? layouts,
        })
      );
    },
    [userWidgets, userSubWidgets, template, layouts, savedState?.layouts]
  );

  useEffect(() => {
    saveDashboardState();
  }, [userWidgets, userSubWidgets, template, saveDashboardState]);

  const handleTemplateChange = useCallback(
    (newTemplate: GridTemplate) => {
      setTemplate(newTemplate);
      localStorage.setItem(
        DASHBOARD_STATE_KEY,
        JSON.stringify({
          userWidgets,
          subWidgets: userSubWidgets,
          template: newTemplate,
          layouts: null,
        })
      );
    },
    [userWidgets]
  );

  const handleEditWidgetSuccess = useCallback(() => {
    setShowAddWidgetModal(false);
  }, []);

  const handleEditVehicleSuccess = useCallback(() => {
    setShowAddNewModal(false);
  }, []);

  return (
    <DashboardDateRangeProvider>
      <PageHeader>
        <div className="flex justify-between">
          <div className="flex flex-col gap-2">
            <div>
              <PageTitle
                title={DASHBOARD_PAGE_TITLE.text}
                description={`${getGreeting()}, ${userName}`}
                icon={DASHBOARD_PAGE_TITLE.icon}
                iconColor={DASHBOARD_PAGE_TITLE.iconColor}
              />
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <DashboardDateRangeChip />
            <Button
              variant="primary"
              children={t("admin.vehicle.search.add")}
              startIcon="/media/icons/plus.svg"
              onClick={() => setShowAddNewModal(true)}
            />
        
            <Button
              variant="secondary"
              startIcon="/media/icons/setting.svg"
              onClick={() => setShowAddWidgetModal(true)}
            ></Button>
          </div>
        </div>
      </PageHeader>



      <div className="pl-4 pt-4">
        <Grid
          key={`${template}-${userWidgets.join(",")}-${JSON.stringify(userSubWidgets)}`}
          ref={gridRef}
          rowHeight={50}
          initialWidgets={widgets}
          initialLayouts={initialLayouts}
          onLayoutChange={(newLayouts) => saveDashboardState(newLayouts)}
        />
      </div>

      <WidgetEditCard
        availableWidgets={DASHBOARD_AVAILABLE_WIDGETS(t)}
        userWidgets={userWidgets}
        setUserWidgets={setUserWidgets}
        userSubWidgets={userSubWidgets}
        setUserSubWidgets={setUserSubWidgets}
        template={template}
        onTemplateChange={handleTemplateChange}
        show={showAddWidgetModal}
        onSuccess={handleEditWidgetSuccess}
        onClose={() => setShowAddWidgetModal(false)}
      />
      <VehicleEditCard
        vehicleId={null}
        show={showAddNewModal}
        onSuccess={handleEditVehicleSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
    </DashboardDateRangeProvider>
  );
};

DashboardPage.route = Routes.DashboardRouter;
export default DashboardPage;
